import { useEffect, useState, useCallback } from 'react'
import { initTelegram, getUser, hapticFeedback } from './lib/telegram'
import { api } from './lib/api'
import type { Deadline } from './lib/api'
import { FocusView } from './components/FocusView'
import { DashboardV2 } from './components/DashboardV2'
import { TasksViewV2 } from './components/TasksViewV2'
import { TeamView } from './components/TeamView'
import { DeadlinesView } from './components/DeadlinesView'
import { LearningView } from './components/LearningView'

type Tab = 'focus' | 'dashboard' | 'tasks' | 'team' | 'deadlines' | 'learning'
type Category = 'work' | 'personal'

function App() {
  const [user, setUser] = useState<{ id: number; first_name: string } | null>(null)
  const [tab, setTab] = useState<Tab>('focus')
  const [category, setCategory] = useState<Category>('work')
  const [loading, setLoading] = useState(true)
  const [deadlines, setDeadlines] = useState<Deadline[]>([])
  const [taskCounts, setTaskCounts] = useState<{ work: number; personal: number }>({ work: 0, personal: 0 })

  const fetchData = useCallback(async () => {
    try {
      const [
        workTasksData, 
        personalTasksData, 
        deadlinesData
      ] = await Promise.all([
        api.getTasks({ status: 'open', category: 'work' }),
        api.getTasks({ status: 'open', category: 'personal' }),
        api.getDeadlines(),
      ])
      setDeadlines(deadlinesData)
      setTaskCounts({
        work: workTasksData.length,
        personal: personalTasksData.length,
      })
    } catch (e) {
      console.error('Failed to fetch data:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    initTelegram()
    const tgUser = getUser()
    if (tgUser) {
      setUser({ id: tgUser.id, first_name: tgUser.first_name })
    }
    fetchData()
  }, [fetchData])

  const switchTab = (newTab: Tab) => {
    hapticFeedback('light')
    setTab(newTab)
  }

  const switchCategory = (newCategory: Category) => {
    hapticFeedback('light')
    setCategory(newCategory)
  }

  const ALLOWED_USER_ID = 1695457813 // Zenith

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <div className="text-4xl">🧘</div>
        <div className="spinner" />
        <p className="text-sm text-muted">Loading dashboard...</p>
      </div>
    )
  }

  if (!user || user.id !== ALLOWED_USER_ID) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center">
        <div className="text-5xl">🔒</div>
        <h1 className="text-xl font-bold">Access Denied</h1>
        <p className="text-sm text-muted max-w-[260px]">
          This is a private dashboard. You don't have permission to view it.
        </p>
        <button
          onClick={() => window.Telegram?.WebApp?.close()}
          className="mt-2 px-6 py-2.5 rounded-xl bg-white/10 text-sm font-medium active:scale-95 transition-transform"
        >
          Close
        </button>
      </div>
    )
  }

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/5">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-accent/20 flex items-center justify-center">
                <span className="text-lg">🧘</span>
              </div>
              <div>
                <h1 className="text-base font-bold tracking-tight">Zen Dashboard V2</h1>
                <p className="text-[11px] text-muted -mt-0.5">
                  {user ? `Hey, ${user.first_name}` : 'ADHD-Optimized'}
                </p>
              </div>
            </div>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent to-[#a855f7] flex items-center justify-center text-xs font-bold">
              {user?.first_name?.[0] || 'Z'}
            </div>
          </div>
          
          {/* Work/Personal Toggle */}
          <div className="flex rounded-xl bg-white/5 p-1">
            <button
              onClick={() => switchCategory('work')}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                category === 'work'
                  ? 'bg-white/10 text-white'
                  : 'text-muted hover:text-white/80'
              }`}
            >
              💼 Work
              {taskCounts.work > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-300 text-xs">
                  {taskCounts.work}
                </span>
              )}
            </button>
            <button
              onClick={() => switchCategory('personal')}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                category === 'personal'
                  ? 'bg-white/10 text-white'
                  : 'text-muted hover:text-white/80'
              }`}
            >
              🏠 Personal
              {taskCounts.personal > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-300 text-xs">
                  {taskCounts.personal}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="py-4">
        {tab === 'focus' && (
          <FocusView 
            category={category} 
            onTaskComplete={fetchData}
          />
        )}
        
        {tab === 'dashboard' && (
          <DashboardV2 
            category={category}
            onSwitchToFocus={() => switchTab('focus')}
            onSwitchToTasks={() => switchTab('tasks')}
          />
        )}
        
        {tab === 'tasks' && (
          <TasksViewV2 
            category={category}
            onRefresh={fetchData}
          />
        )}
        
        {tab === 'team' && (
          <TeamView 
            category={category}
            onRefresh={fetchData}
          />
        )}
        
        {tab === 'deadlines' && (
          <DeadlinesView deadlines={deadlines} loading={false} />
        )}
        
        {tab === 'learning' && (
          <LearningView />
        )}
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 glass border-t border-white/5 z-50">
        <div className="flex items-center justify-around py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          {([
            { id: 'focus' as Tab, icon: '🎯', label: 'Focus' },
            { id: 'dashboard' as Tab, icon: '🏠', label: 'Home' },
            { id: 'tasks' as Tab, icon: '✅', label: 'Tasks' },
            { id: 'team' as Tab, icon: '👥', label: 'Team' },
            { id: 'deadlines' as Tab, icon: '⏰', label: 'Deadlines' },
            { id: 'learning' as Tab, icon: '🧠', label: 'Learning' },
          ]).map(({ id, icon, label }) => (
            <button
              key={id}
              onClick={() => switchTab(id)}
              className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg transition-all active:scale-95 ${
                tab === id ? 'text-accent-light' : 'text-muted'
              }`}
            >
              <span className="text-lg">{icon}</span>
              <span className="text-[10px] font-medium">{label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  )
}

export default App
